import type { Cart, CartItem, RemoveFromCartRequest, UpdateCartRequest } from "@/types";
import * as Sentry from "@sentry/react";

const devCartStoragePrefix = "nile_checkout_dev_cart:";

function getDevCartStorageKey(sessionId: string): string {
  return `${devCartStoragePrefix}${sessionId}`;
}

interface BotCartItem {
  productId: string;
  title: string;
  quantity: number;
  price_cents: number;
}

interface BotCartData {
  items: BotCartItem[];
}

function calculateCartTotals(items: CartItem[]): { subtotalCents: number; itemCount: number } {
  const subtotalCents = items.reduce((sum, item) => sum + item.line_total_cents, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return { subtotalCents, itemCount };
}

function readCartDataFromUrl(): BotCartData | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const cartDataEncoded = params.get("cart_data");
    if (!cartDataEncoded) return null;

    const decoded = atob(cartDataEncoded);
    const cartData = JSON.parse(decoded) as BotCartData;
    return cartData;
  } catch (error) {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { component: "DevCart", action: "readCartDataFromUrl" },
      });
    }
    return null;
  }
}

function convertBotCartToCart(sessionId: string, botCart: BotCartData): Cart {
  const items: CartItem[] = botCart.items.map((item, index) => ({
    id: `bot_item_${item.productId}_${index}`,
    product_id: item.productId,
    sku: item.productId,
    title: item.title,
    image_url: null,
    quantity: item.quantity,
    unit_price_cents: item.price_cents,
    line_total_cents: item.price_cents * item.quantity,
    in_stock: true,
    selected_variant: null,
  }));

  const { subtotalCents, itemCount } = calculateCartTotals(items);

  return {
    session_id: sessionId,
    items,
    subtotal_cents: subtotalCents,
    currency: "ZAR",
    item_count: itemCount,
  };
}

export function isDevCartEnabled(): boolean {
  if (import.meta.env.DEV) {
    try {
      const hasSessionToken = localStorage.getItem("session_token") != null;
      return !hasSessionToken;
    } catch (error) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "DevCart", action: "isDevCartEnabled" },
        });
      }
      return true;
    }
  }
  // return import.meta.env.DEV;
  return false;
}

export function hasStoredDevCart(sessionId: string): boolean {
  try {
    return localStorage.getItem(getDevCartStorageKey(sessionId)) != null;
  } catch (error) {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { component: "DevCart", action: "hasStoredDevCart" },
      });
    }
    return false;
  }
}

function normalizeCart(cart: Cart): Cart {
  const normalizedItems: CartItem[] = cart.items.map((item) => ({
    ...item,
    line_total_cents: item.unit_price_cents * item.quantity,
  }));

  const { subtotalCents, itemCount } = calculateCartTotals(normalizedItems);

  return {
    ...cart,
    items: normalizedItems,
    subtotal_cents: subtotalCents,
    item_count: itemCount,
  };
}

function createSeedDevCart(sessionId: string): Cart {
  // Use products matching whatsapp-bot.ts MOCK_PRODUCTS
  const items: CartItem[] = [
    {
      id: "dev_item_1",
      product_id: "550e8400-e29b-41d4-a716-446655440001",
      sku: "550e8400-e29b-41d4-a716-446655440001",
      title: "Vitamin C Serum",
      image_url: null,
      quantity: 1,
      unit_price_cents: 29999,
      line_total_cents: 29999,
      in_stock: true,
      selected_variant: null,
    },
    {
      id: "dev_item_2",
      product_id: "550e8400-e29b-41d4-a716-446655440002",
      sku: "550e8400-e29b-41d4-a716-446655440002",
      title: "Hyaluronic Acid Moisturizer",
      image_url: null,
      quantity: 1,
      unit_price_cents: 19999,
      line_total_cents: 19999,
      in_stock: true,
      selected_variant: null,
    },
  ];

  const { subtotalCents, itemCount } = calculateCartTotals(items);

  return {
    session_id: sessionId,
    items,
    subtotal_cents: subtotalCents,
    currency: "ZAR",
    item_count: itemCount,
  };
}

function readStoredDevCart(sessionId: string): Cart | null {
  try {
    const raw = localStorage.getItem(getDevCartStorageKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cart;
    if (!parsed || parsed.session_id !== sessionId || !Array.isArray(parsed.items)) return null;
    return normalizeCart(parsed);
  } catch (error) {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { component: "DevCart", action: "readStoredDevCart" },
      });
    }
    return null;
  }
}

function writeStoredDevCart(cart: Cart): void {
  try {
    localStorage.setItem(getDevCartStorageKey(cart.session_id), JSON.stringify(cart));
  } catch (error) {
    // ignore storage failures (e.g. private mode / quota)
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { component: "DevCart", action: "writeStoredDevCart" },
      });
    }
  }
}

export function getOrCreateDevCart(sessionId: string): Cart {
  // First check if we have cart data from URL (WhatsApp bot)
  const botCartData = readCartDataFromUrl();
  if (botCartData && botCartData.items.length > 0) {
    const cart = convertBotCartToCart(sessionId, botCartData);
    writeStoredDevCart(cart);
    // Clean up URL param after reading
    const url = new URL(window.location.href);
    url.searchParams.delete("cart_data");
    window.history.replaceState({}, "", url.toString());
    return cart;
  }

  // Check localStorage for existing cart
  const existing = readStoredDevCart(sessionId);
  if (existing) return existing;

  // Fall back to seed data
  const seeded = createSeedDevCart(sessionId);
  writeStoredDevCart(seeded);
  return seeded;
}

export function updateDevCartItem(request: UpdateCartRequest): Cart {
  const cart = getOrCreateDevCart(request.session_id);
  const existingIndex = cart.items.findIndex((item) => item.product_id === request.product_id);

  let nextItems: CartItem[];
  if (existingIndex >= 0) {
    if (request.quantity <= 0) {
      nextItems = cart.items.filter((item) => item.product_id !== request.product_id);
    } else {
      nextItems = cart.items.map((item) =>
        item.product_id === request.product_id ? { ...item, quantity: request.quantity } : item
      );
    }
  } else {
    // If a product isn't in the cart yet, add a simple placeholder item.
    nextItems = [
      ...cart.items,
      {
        id: `dev_item_${request.product_id}`,
        product_id: request.product_id,
        sku: request.product_id,
        title: `Product ${request.product_id}`,
        image_url: null,
        quantity: Math.max(1, request.quantity),
        unit_price_cents: 10000,
        line_total_cents: 10000 * Math.max(1, request.quantity),
        in_stock: true,
        selected_variant: null,
      },
    ];
  }

  const nextCart = normalizeCart({ ...cart, items: nextItems });
  writeStoredDevCart(nextCart);
  return nextCart;
}

export function removeDevCartItem(request: RemoveFromCartRequest): Cart {
  const cart = getOrCreateDevCart(request.session_id);
  const nextItems = cart.items.filter((item) => item.product_id !== request.product_id);
  const nextCart = normalizeCart({ ...cart, items: nextItems });
  writeStoredDevCart(nextCart);
  return nextCart;
}
